import { useContext } from 'react';
import { ReportContext } from '../../../context/ReportContext';
import ColorCard from '../../Cards/ColorCard';
import NoteCard from '../../Cards/NoteCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const percentFormatter = (value) => {
    return Math.round(value * 100) + '%';
};

const sampleSizeFormatter = (value) => {
    return 'n=' + Number(value).toLocaleString();
};

const hasSampleSize = (sampleSize) =>
    sampleSize !== null && sampleSize !== undefined && !isNaN(sampleSize);

const StatisticItem = ({ backgroundColor, textColor, name, details, value, sampleSize, hasSufficientData = true }) => {
    return (
        <div
            className={`flex justify-between items-center py-3 px-4 ${backgroundColor} rounded-lg`}
        ><div style={{ maxWidth: '80%' }}>
            <span className="font-medium text-gray-700">{name}</span>
            {details && <p className="text-sm text-gray-700">{details}</p>}
        </div>
            
            {hasSufficientData ? (
                <div className="text-right">
                    <span className={`text-lg font-semibold ${textColor}`}>
                        {percentFormatter(value)}
                    </span>
                    {hasSampleSize(sampleSize) && (
                        <p className="text-xs text-gray-600 whitespace-nowrap">
                            {sampleSizeFormatter(sampleSize)}
                        </p>
                    )}
                </div>
            ) : (
                <div className="text-sm font-semibold bg-yellow-50 text-yellow-800 rounded-lg px-4 py-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3" />
                    Insufficient data
                </div>
            )}
        </div>
    );
};

const InsufficientData = () => (
    <div className="flex items-center p-4 bg-yellow-50 text-yellow-800 rounded-lg">
        <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 mr-3" />
        <div>
            <p className="font-semibold">Insufficient Data</p>
            <p>Please try a longer date range</p>
        </div>
    </div>
);

const Dashboard = () => {
    const { reportData } = useContext(ReportContext);

    return (
        <div className="space-y-8 p-6 max-w-screen-md">
            {/* Informational Note */}
            <NoteCard>
                Data reflects activity from users who registered during the selected time
                range.
            </NoteCard>
            {/* Financial Behavior Changes Section */}
            <ColorCard
                backgroundColor="gray"
                title="Financial Behavior Changes from Wellness Checkup Responses"
                description="Positive financial habits adopted by users"
            >
                <div className="grid grid-cols-1 gap-4">
                    <StatisticItem
                        backgroundColor="bg-gray-50"
                        textColor="text-blue-900"
                        name="Established an Emergency Savings Fund"
                        details="Percentage of users who, after initially reported lacking a proper emergency fund, went on to build savings equal to 3–6 months of essential expenses."
                        value={reportData.percentEmergencyFund}
                        sampleSize={reportData.sampleSizeEmergencyFund}
                        hasSufficientData={reportData.sufficientDataEmergencyFund}
                    />
                    <StatisticItem
                        backgroundColor="bg-gray-50"
                        textColor="text-blue-900"
                        name="Started Budgeting"
                        details="Percentage of users transitioning from reporting no budget use to actively budgeting over time."
                        value={reportData.percentBudget}
                        sampleSize={reportData.sampleSizeBudget}
                        hasSufficientData={reportData.sufficientDataBudget}
                    />
                    <StatisticItem
                        backgroundColor="bg-gray-50"
                        textColor="text-blue-900"
                        name="Stopped Monthly Debt Accumulation"
                        details="Percentage of users who first reported needing debt to cover monthly expenses later reported they can cover them without borrowing. "
                        value={reportData.percentAbleToPayBills}
                        sampleSize={reportData.sampleSizeAbleToPayBills}
                        hasSufficientData={reportData.sufficientDataAbleToPayBills}
                    />
                    <StatisticItem
                        backgroundColor="bg-gray-50"
                        textColor="text-blue-900"
                        name="Started Retirement Contributions"
                        details="Percentage of users who began contributing to their employer-sponsored retirement plan after initially reporting no contributions."
                        value={reportData.percentRetirement}
                        sampleSize={reportData.sampleSizeRetirement}
                        hasSufficientData={reportData.sufficientDataRetirement}
                    />
                    <StatisticItem
                        backgroundColor="bg-gray-50"
                        textColor="text-blue-900"
                        name="Achieved Positive Cashflow"
                        details="Percentage of users who reported having a positive cash flow after initially reporting a negative cash flow. Percentage of users who initially reported no surplus funds for saving or investing and later reported positive monthly cash flow."
                        value={reportData.percentLeftoverMoney}
                        sampleSize={reportData.sampleSizeLeftoverMoney}
                        hasSufficientData={reportData.sufficientDataLeftoverMoney}
                    />
                </div>
            </ColorCard>

            {/* Platform Helpfulness Section */}
            <ColorCard
                backgroundColor="gray"
                title="Platform Helpfulness Feedback from Feedback Modal"
                description="User provided feedback on how they feel about the platform"
            >
                {reportData.sufficientDataPlatformFeedback ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <StatisticItem
                            backgroundColor="bg-green-50"
                            textColor="text-green-600"
                            name="Found Platform Helpful"
                            value={
                                reportData.percentPlatformExtremelyHelpful +
                                reportData.percentPlatformVeryHelpful +
                                reportData.percentPlatformModeratelyHelpful +
                                reportData.percentPlatformSlightlyHelpful
                            }
                            sampleSize={reportData.sampleSizePlatformFeedback}
                        />
                        <StatisticItem
                            backgroundColor="bg-red-50"
                            textColor="text-red-600"
                            name="Found Platform Not Helpful"
                            value={reportData.percentPlatformNotHelpful}
                            sampleSize={reportData.sampleSizePlatformFeedback}
                        />
                        <div className="lg:col-span-2 mt-2 text-center text-gray-600">
                            <strong>
                                {percentFormatter(
                                    reportData.percentPlatformExtremelyHelpful +
                                        reportData.percentPlatformVeryHelpful
                                )}
                            </strong>{' '}
                            of users found the platform very or extremely helpful
                        </div>
                    </div>
                ) : (
                    <InsufficientData />
                )}
            </ColorCard>

            {/* Content Helpfulness Section */}
            <ColorCard
                backgroundColor="gray"
                title="Content Quality Feedback from Content Footer"
                description="User provided feedback on whether they indicated content was helpful or not"
            >
                {reportData.sufficientDataContentFeedback ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <StatisticItem
                            backgroundColor="bg-green-50"
                            textColor="text-green-600"
                            name="Found Content Helpful"
                            value={reportData.percentContentHelpful}
                            sampleSize={reportData.sampleSizeContentFeedback}
                        />
                        <StatisticItem
                            backgroundColor="bg-red-50"
                            textColor="text-red-600"
                            name="Did Not Find Content Helpful"
                            value={reportData.percentContentNotHelpful}
                            sampleSize={reportData.sampleSizeContentFeedback}
                        />
                    </div>
                ) : (
                    <InsufficientData />
                )}
            </ColorCard>
        </div>
    );
};

export default Dashboard;
